import React, { useEffect, useState } from "react";
import { apiFetch } from "./api";

export default function Groupings({ onBack }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingIds, setSavingIds] = useState(new Set());
  const [dirtyIds, setDirtyIds] = useState(new Set());
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [newCompanyName, setNewCompanyName] = useState("");
  const [adding, setAdding] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addError, setAddError] = useState("");

  useEffect(() => {
    loadClients();
  }, []);

  // Esc closes the "Lisa ettevõte" modal (except mid-add).
  useEffect(() => {
    if (!showAddModal) return;
    function onKey(e) {
      if (e.key === "Escape" && !adding) setShowAddModal(false);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showAddModal, adding]);

  function loadClients() {
    setLoading(true);
    setError("");
    setSuccessMessage("");

    apiFetch("/api/clients")
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        const mapped = data.map((c) => ({
          id: Number(c.id),
          company_name: c.company_name,
          company_reg_code: c.company_reg_code,
          company_email: c.company_email,
          email_text: c.email_text,
          grupp: Number(c.grupp), // always valued (NOT NULL); own id = standalone
        }));

        setClients(mapped);
        setDirtyIds(new Set());
      })
      .catch((err) => {
        console.error("Failed to load clients", err);
        setError("Klientide laadimine ebaõnnestus.");
      })
      .finally(() => setLoading(false));
  }

  function handleGroupChange(clientId, newValue) {
    const newGroupId = Number(newValue);

    setClients((prev) =>
      prev.map((client) =>
        client.id === clientId
          ? { ...client, grupp: newGroupId }
          : client
      )
    );

    setDirtyIds((prev) => {
      const next = new Set(prev);
      next.add(clientId);
      return next;
    });
  }

  async function saveClientGroup(client) {
    setError("");
    setSuccessMessage("");

    setSavingIds((prev) => {
      const next = new Set(prev);
      next.add(client.id);
      return next;
    });

    try {
      const res = await apiFetch(
        `/api/clients/${client.id}/group`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ grupp: Number(client.grupp) }),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || "Salvestamine ebaõnnestus");
      }

      setDirtyIds((prev) => {
        const next = new Set(prev);
        next.delete(client.id);
        return next;
      });

      setSuccessMessage(`Salvestatud: ${client.company_name}`);
    } catch (err) {
      console.error("Failed to save group", err);
      setError(`Salvestamine ebaõnnestus (${client.company_name})`);
    } finally {
      setSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(client.id);
        return next;
      });
    }
  }

  async function createCompany() {
    const name = newCompanyName.trim();
    if (!name) return;

    setAdding(true);
    setAddError("");

    try {
      const res = await apiFetch("/api/clients", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ company_name: name }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `Lisamine ebaõnnestus (HTTP ${res.status})`);

      setNewCompanyName("");
      setShowAddModal(false);
      loadClients();
      setSuccessMessage(`Lisatud: ${name}`);
    } catch (err) {
      console.error("Failed to add company", err);
      setAddError(err.message || "Ettevõtte lisamine ebaõnnestus.");
    } finally {
      setAdding(false);
    }
  }

  return (
    <div style={styles.page}>
      <div style={styles.topBar}>
        <div>
          <h1 style={styles.title}>Gruppeering</h1>
          <div style={styles.subtitle}>
            Vali igale kliendile grupp rippmenüüst ja salvesta muudatused.
          </div>
        </div>

        <div style={styles.topActions}>
          <button
            onClick={() => {
              setNewCompanyName("");
              setAddError("");
              setShowAddModal(true);
            }}
            style={styles.primaryBtn}
            type="button"
          >
            Lisa ettevõte
          </button>

          <button onClick={onBack} style={styles.secondaryBtn} type="button">
            ← Tagasi
          </button>
        </div>
      </div>

      <div style={styles.card}>
        {loading ? (
          <div style={styles.infoText}>Laen andmeid…</div>
        ) : error ? (
          <div style={styles.errorText}>{error}</div>
        ) : clients.length === 0 ? (
          <div style={styles.infoText}>Andmeid ei leitud.</div>
        ) : (
          <>
            {successMessage && (
              <div style={styles.successText}>{successMessage}</div>
            )}

            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Kliendi nimi</th>
                    <th style={styles.th}>Grupp</th>
                    <th style={styles.th}>Tegevus</th>
                  </tr>
                </thead>

                <tbody>
                  {clients.map((client) => (
                    <tr key={client.id}>
                      <td style={styles.td}>{client.company_name}</td>

                      <td style={styles.td}>
                        <select
                          value={client.grupp}
                          onChange={(e) =>
                            handleGroupChange(client.id, e.target.value)
                          }
                          style={styles.select}
                        >
                          {clients.map((optionClient) => (
                            <option key={optionClient.id} value={optionClient.id}>
                              {optionClient.company_name}
                            </option>
                          ))}
                        </select>
                      </td>

                      <td style={styles.td}>
                        <button
                          type="button"
                          style={
                            dirtyIds.has(client.id)
                              ? styles.primaryBtnSmall
                              : styles.disabledBtn
                          }
                          disabled={!dirtyIds.has(client.id) || savingIds.has(client.id)}
                          onClick={() => saveClientGroup(client)}
                        >
                          {savingIds.has(client.id) ? "Salvestan..." : "Salvesta"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {showAddModal && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>Lisa ettevõte</h3>
            <input
              type="text"
              placeholder="Ettevõtte nimi"
              value={newCompanyName}
              onChange={(e) => setNewCompanyName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") createCompany();
              }}
              autoFocus
              style={styles.modalInput}
            />
            {addError && <div style={styles.errorText}>{addError}</div>}
            <div style={styles.modalActions}>
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                style={styles.secondaryBtn}
                disabled={adding}
              >
                Tühista
              </button>
              <button
                type="button"
                onClick={createCompany}
                disabled={adding || !newCompanyName.trim()}
                style={adding || !newCompanyName.trim() ? styles.disabledBtn : styles.primaryBtn}
              >
                {adding ? "Lisan…" : "Lisa"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    backgroundColor: "#F3F6FD",
    fontFamily: "'DM Sans', sans-serif",
    padding: "28px",
  },

  topBar: {
    maxWidth: "1200px",
    margin: "0 auto 20px auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
  },

  topActions: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
  },

  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },

  modal: {
    background: "white",
    borderRadius: "14px",
    padding: "24px",
    width: "380px",
    boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
  },

  modalTitle: {
    margin: "0 0 16px 0",
    color: "#0D1F4D",
    fontSize: "18px",
  },

  modalInput: {
    width: "100%",
    boxSizing: "border-box",
    borderRadius: "10px",
    border: "1px solid #8CA5E6",
    padding: "10px 12px",
    outline: "none",
    background: "#fff",
    fontSize: "14px",
    fontFamily: "'DM Sans', sans-serif",
    marginBottom: "6px",
  },

  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    marginTop: "12px",
  },

  title: {
    margin: 0,
    color: "#0D1F4D",
    fontSize: "28px",
  },

  subtitle: {
    marginTop: "6px",
    color: "#555",
    fontSize: "14px",
  },

  card: {
    maxWidth: "1200px",
    margin: "0 auto",
    background: "white",
    borderRadius: "16px",
    padding: "18px",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
  },

  tableWrap: {
    overflowX: "auto",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
  },

  th: {
    textAlign: "left",
    padding: "12px 10px",
    borderBottom: "1px solid #E6ECF8",
    color: "#0D1F4D",
    fontWeight: 700,
    fontSize: "14px",
    background: "#F8FAFF",
  },

  td: {
    padding: "12px 10px",
    borderBottom: "1px solid #E6ECF8",
    fontSize: "14px",
    color: "#0D1F4D",
    verticalAlign: "middle",
  },

  select: {
    width: "100%",
    minWidth: "260px",
    borderRadius: "10px",
    border: "1px solid #E6ECF8",
    padding: "10px 12px",
    background: "#fff",
    outline: "none",
  },

  primaryBtn: {
    backgroundColor: "#0D1F4D",
    color: "white",
    border: "none",
    borderRadius: "8px",
    padding: "10px 16px",
    fontWeight: 600,
    cursor: "pointer",
  },

  primaryBtnSmall: {
    backgroundColor: "#0D1F4D",
    color: "white",
    border: "none",
    borderRadius: "8px",
    padding: "8px 12px",
    fontWeight: 600,
    cursor: "pointer",
  },

  secondaryBtn: {
    backgroundColor: "#8CA5E6",
    color: "white",
    border: "none",
    borderRadius: "8px",
    padding: "10px 14px",
    fontWeight: 600,
    cursor: "pointer",
  },

  disabledBtn: {
    backgroundColor: "#D8E1F7",
    color: "#6C7A9A",
    border: "none",
    borderRadius: "8px",
    padding: "8px 12px",
    fontWeight: 600,
    cursor: "not-allowed",
  },

  infoText: {
    color: "#555",
    padding: "12px 4px",
  },

  errorText: {
    color: "#C0392B",
    padding: "12px 4px",
    fontWeight: 600,
  },

  successText: {
    color: "#1E8449",
    padding: "0 0 12px 0",
    fontWeight: 600,
  },
};
